import React, { useEffect, useState, useRef, useImperativeHandle } from 'react';
import { MapContainer, useMap, Marker, Tooltip, GeoJSON } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L, { geoJSON } from 'leaflet';
import ColorPaletteSelection from './ColorPaletteSelection';
//import LayerSelection from './LayerSelection';
import * as echarts from 'echarts';
import { FaChevronUp, FaChevronDown, FaChevronLeft, FaChevronRight, FaHome, FaCircle, FaPlus, FaMinus } from 'react-icons/fa';

//================== markers for ulg map ======================
// 🔹 Default icon for now
const defaultIcon = new L.Icon({
  iconUrl: "https://cdn-icons-png.flaticon.com/128/7945/7945007.png", // your placeholder icon
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

// ================= Patterns =================
const PatternDefs = () => {
  const [patternDataUrls, setPatternDataUrls] = useState({});

  useEffect(() => {
    const patternPaths = {
      sandyPattern: '/newpatterns/p1-2.png',
      clayPattern: '/patterns/p2.png',
      loamPattern: '/newpatterns/p3-2.png',
      rockyPattern: '/patterns/p4.png',
      betaPattern: '/patterns/p5.png',
      noPattern: '/patterns/p6.png'
    };

    const loadPatterns = async () => {
      const urls = {};
      for (const [key, path] of Object.entries(patternPaths)) {
        try {
          const response = await fetch(path);
          const blob = await response.blob();
          const reader = new FileReader();
          reader.onloadend = () => {
            urls[key] = reader.result;
            setPatternDataUrls(prev => ({ ...prev, [key]: reader.result }));
          };
          reader.readAsDataURL(blob);
        } catch (error) {
          console.error(`Failed to load pattern ${key}:`, error);
        }
      }
    };

    loadPatterns();
  }, []);

  return (
    <svg width="0" height="0" style={{ position: 'absolute' }}>
      <defs>
        <pattern id="sandyPattern" patternUnits="userSpaceOnUse" width="20" height="20">
          {patternDataUrls.sandyPattern && (
            <image href={patternDataUrls.sandyPattern} width="20" height="20" />
          )}
        </pattern>
        <pattern id="clayPattern" patternUnits="userSpaceOnUse" width="20" height="20">
          {patternDataUrls.clayPattern && (
            <image href={patternDataUrls.clayPattern} width="20" height="20" />
          )}
        </pattern>
        <pattern id="loamPattern" patternUnits="userSpaceOnUse" width="20" height="20">
          {patternDataUrls.loamPattern && (
            <image href={patternDataUrls.loamPattern} width="20" height="20" />
          )}
        </pattern>
        <pattern id="rockyPattern" patternUnits="userSpaceOnUse" width="20" height="20">
          {patternDataUrls.rockyPattern && (
            <image href={patternDataUrls.rockyPattern} width="20" height="20" />
          )}
        </pattern>
        <pattern id="betaPattern" patternUnits="userSpaceOnUse" width="20" height="20">
          {patternDataUrls.betaPattern && (
            <image href={patternDataUrls.betaPattern} width="20" height="20" />
          )}
        </pattern>
        <pattern id="noPattern" patternUnits="userSpaceOnUse" width="20" height="20">
          {patternDataUrls.noPattern && (
            <image href={patternDataUrls.noPattern} width="20" height="20" />
          )}
        </pattern>
      </defs>
    </svg>
  );
};
const patternIds = ['sandyPattern', 'clayPattern', 'loamPattern', 'rockyPattern', 'betaPattern', 'noPattern'];

const getPatternIdDynamic = (value, breaks) => {
  // Handle null/undefined values
  if (value == null) return 'noPattern';

  // Ensure we have valid breaks array
  if (
    !Array.isArray(breaks) ||
    breaks.some(b => isNaN(parseFloat(b)))
  ) {
    return '#ffffff';
  }
  if (!breaks || breaks.length < 2) return 'noPattern';

  // For 6 breaks (0-5 indices), we get 5 ranges + 1 for no data
  // breaks[0] to breaks[1] -> patternIds[0] (sandyPattern)
  // breaks[1] to breaks[2] -> patternIds[1] (clayPattern)
  // breaks[2] to breaks[3] -> patternIds[2] (loamPattern)
  // breaks[3] to breaks[4] -> patternIds[3] (rockyPattern)
  // breaks[4] to breaks[5] -> patternIds[4] (betaPattern)
  // No data -> patternIds[5] (noPattern)

  if (breaks.length === 6) {
    // 6 breaks = 5 ranges
    if (value < breaks[1]) return patternIds[0]; // sandyPattern
    if (value < breaks[2]) return patternIds[1]; // clayPattern
    if (value < breaks[3]) return patternIds[2]; // loamPattern
    if (value < breaks[4]) return patternIds[3]; // rockyPattern
    if (value < breaks[5]) return patternIds[4]; // betaPattern
    return patternIds[4]; // Values >= breaks[5] use last pattern
  }

  // Fallback for different break lengths
  if (breaks.length > 2) {
    const numRanges = breaks.length - 1;
    for (let i = 0; i < numRanges && i < patternIds.length - 1; i++) {
      if (value < breaks[i + 1]) {
        return patternIds[i];
      }
    }
    // Return the last data pattern (not noPattern) for values above all breaks
    return patternIds[Math.min(numRanges - 1, patternIds.length - 2)];
  }

  return 'noPattern';
};

// ================= Fix Leaflet Icons =================
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

// ================= MapContent =================
const MapContent = ({ geoJsonData, statesData, selectedPalette,
  indicator3LayerRef, indicator4LayerRef, indicator5LayerRef, showIndicator3, showIndicator4, showIndicator5, showColorLayer,
  showPatternLayer, setSelectedPalette, setShowIndicator3, setShowIndicator4, setShowIndicator5, setShowColorLayer, setShowPatternLayer,
  setMapConfigData, onBreaksCalculated, onIndicatorBreaksCalculated, showPieChart, setShowPieChart,setUlbMapBreak,showController,
}) => {
  const map = useMap();
  const popupLayerRef = useRef(null);
  const colorLayerRef = useRef(null);
  const patternLayerRef = useRef(null);
  const echartsLayerRef = useRef(null); // Ref for the ECharts layer group

//   useEffect(() => {
//     if (!indicator3LayerRef.current) {
//       indicator3LayerRef.current = L.layerGroup().addTo(map);
//     }
//     if (!indicator4LayerRef.current) {
//       indicator4LayerRef.current = L.layerGroup().addTo(map);
//     }
//     if (!indicator5LayerRef.current) {
//       indicator5LayerRef.current = L.layerGroup().addTo(map);
//     }
//     if (!echartsLayerRef.current) {
//       echartsLayerRef.current = L.layerGroup().addTo(map);
//     }

//     return () => {
//       if (indicator3LayerRef.current) {
//         indicator3LayerRef.current.clearLayers();
//         indicator3LayerRef.current.remove();
//         indicator3LayerRef.current = null;
//       }
//       if (indicator4LayerRef.current) {
//         indicator4LayerRef.current.clearLayers();
//         indicator4LayerRef.current.remove();
//         indicator4LayerRef.current = null;
//       }
//       if (indicator5LayerRef.current) {
//         indicator5LayerRef.current.clearLayers();
//         indicator5LayerRef.current.remove();
//         indicator5LayerRef.current = null;
//       }
//       if (echartsLayerRef.current) {
//         echartsLayerRef.current.clearLayers();
//         echartsLayerRef.current.remove();
//         echartsLayerRef.current = null;
//       }
//     };
//   }, [map, indicator3LayerRef, indicator4LayerRef, indicator5LayerRef]);

//   // pie chart popup for map 
  // const showPiePopup = (latlng, state) => {
  //   //const indicator_pie_code = indicator?.subIndicator?.code || indicator?.code;
  //   const indicator = setMapConfigData?.indicator; // assuming 1003 is the one you want
  //   const icode = indicator?.code;
  //   const sicode = indicator?.subIndicator?.code ;
  //   const indicator_pie_code = icode



  //   const indicatorName = indicator?.label || indicator?.name || 'Indicator';

  //   // Get the data for the pie chart
  //   const chartData = state[indicator_pie_code];

  //   // Create wrapper div
  //   const wrapper = document.createElement('div');
  //   wrapper.className = 'text-center';

  //   // Title for the indicator name
  //   const title = document.createElement('h6');
  //   title.textContent = indicatorName;  // Set title to indicator name
  //   title.className = 'fw-bold mb-2';

  //   // Subtitle for the state name
  //   const subtitle = document.createElement('h6');
  //   subtitle.textContent = state?.name || '';
  //   subtitle.className = 'mb-3';

  //   // Chart container
  //   const chartContainer = document.createElement('div');
  //   chartContainer.className = '';
  //   chartContainer.style.width = '30vh';
  //   chartContainer.style.height = '20vh';

  //   // Append title, subtitle, and chart container
  //   wrapper.appendChild(title);
  //   wrapper.appendChild(subtitle);
  //   wrapper.appendChild(chartContainer);

  //   setTimeout(() => {
  //     const chartInstance = echarts.init(chartContainer);

  //     const option = {
  //       // tooltip: {
  //       //   trigger: 'item',
  //       //   formatter: '{b}: {c} ({d}%)'
  //       // },
  //       series: [
  //         {
  //           name: indicatorName,
  //           type: 'pie',
  //           radius: ['25%', '50%'],   // donut style, can adjust radius to show labels better
  //           avoidLabelOverlap: true,
  //           label: {
  //             show: true,
  //             formatter: '{b}: {d}%',
  //             fontSize: 10,
  //             position: 'outside'  // show labels outside slices for better readability
  //           },
  //           emphasis: {
  //             label: {
  //               show: true,
  //               fontSize: 12,
  //               fontWeight: 'bold'
  //             }
  //           },
  //           itemStyle: {
  //             borderColor: '#000',
  //             borderWidth: 1
  //           },
  //           labelLine: {
  //             show: true
  //           },
  //           data: chartData.map(item => ({
  //             value: Object.values(item)[0],
  //             name: Object.keys(item)[0]
  //           }))
  //         }
  //       ]
  //     };
  //     console.log(option,"piechart");

  //     chartInstance.setOption(option);
  //   }, 0);

  //   // Show the popup on map
  //   L.popup()
  //     .setLatLng(latlng)
  //     .setContent(wrapper)
  //     .openOn(map);
  // }

  // function to split any long text into chunks (≤ maxLength)
  // and break ONLY at allowed characters (e.g. space, punctuation)
const smartWrapText = (text, maxLength, allowedBreakChars, includeBreakChar = true) => {
  if (!text || text.length === 0 || maxLength < 1) return [''];
  if (!allowedBreakChars) return [text];

  // Escape regex special chars
  const escaped = allowedBreakChars.replace(/[\\^$*+?.|[\]{}]/g, '\\$&');
  const breakRegex = new RegExp(`[${escaped}]`);

  const chunks = [];
  let start = 0;

  while (start < text.length) {
    const remaining = text.length - start;

    // If remaining text fits, take it all
    if (remaining <= maxLength) {
      chunks.push(text.substring(start));
      break;
    }

    let breakPos = -1;

    // Search backward from maxLength for the last allowed break character
    for (let i = start + maxLength; i >= start; i--) {
      if (breakRegex.test(text[i])) {
        breakPos = i;
        break;
      }
    }

    if (breakPos !== -1) {
      if (includeBreakChar) {
        // Include the punctuation/space in current line (natural reading)
        chunks.push(text.substring(start, breakPos + 1));
        start = breakPos + 1;
      } else {
        // Break before the character
        chunks.push(text.substring(start, breakPos));
        start = breakPos;
      }
    } else {
      // No safe break found → fall back to hard break at maxLength
      // (prevents infinite loop and overly long lines)
      chunks.push(text.substring(start, start + maxLength));
      start += maxLength;
    }
  }
  return chunks;
};

const showPiePopup = (latlng, state) => {
  const indicator = setMapConfigData?.indicator;
  const indicatorName = indicator?.label || indicator?.name || 'Indicator';
  const subIndicator = indicator?.subIndicator?.label || indicator?.subIndicator?.name || '';

  // ---- Find longest word length ----
  const longestWordLength = Math.max(...subIndicator.split(/\s+/).map(w => w.length));

  // ---- Default sizes in vh ----
  let popupWidth = "70vh";
  let popupHeight = "60vh";
  let maxCharsPerLine = 45; // adjust based on popup width
          

  // if (longestWordLength <= 18) {
  //   popupWidth = "40vh";
  //   popupHeight = "30vh";
  //   maxCharsPerLine  = 15;
  // } else if (longestWordLength <= 40) {
  //   popupWidth = "50vh";
  //   popupHeight = "40vh";
  //   maxCharsPerLine  = 25;
  // } else if (longestWordLength <= 60) {
  //   popupWidth = "60vh";
  //   popupHeight = "50vh";
  //   maxCharsPerLine  = 35;
  // } else {
  //   popupWidth = "70vh";
  //   popupHeight = "60vh";
  //   maxCharsPerLine  = 45;
  // }

  const indicator_pie_code = indicator?.code;
  const chartData = state[indicator_pie_code];

  const wrapper = document.createElement('div');
  wrapper.className = 'text-center';
  wrapper.style.width = popupWidth;
  wrapper.style.height = popupHeight;
  wrapper.style.padding = '8px';

  const title = document.createElement('h6');
  title.textContent = indicatorName;
  title.className = 'fw-bold ';

  const subtitle = document.createElement('h6');
  subtitle.textContent = state?.name || '';
  subtitle.className = 'mb-1';

  const chartContainer = document.createElement('div');
  chartContainer.style.width = "calc(100% )";
  chartContainer.style.height = "calc(100% - 60px)";

  wrapper.appendChild(title);
  wrapper.appendChild(subtitle);
  wrapper.appendChild(chartContainer);

  setTimeout(() => {
    const chartInstance = echarts.init(chartContainer);

chartInstance.setOption({
  series: [
    {
      name: indicatorName,
      type: 'pie',
      radius: ['25%', '50%'],
      label: {
        show: true,
        position: 'outside',
        fontSize: 10,
        overflow: 'break', // important for wrapping
        rich: {
          multiLine: {
            fontSize: 10,
            lineHeight: 12
          }
        },
        formatter: function(params) {
          const name = params.name + `: ${params.percent}%`;
          
          // Split into chunks for line break

          const chunks = smartWrapText(name, maxCharsPerLine, " ,;:!?");
          // let start = 0;
          // while (start < name.length) {
          //   const regex = new RegExp(`[ ;/\@&:]`); // Create a regex like /[oe]/
          //   const index_o = name.search(regex);
          //   chunks.push(name.substring(start, start + maxCharsPerLine));
          //   start += maxCharsPerLine;
          // }

          // Join with \n and append percent
          return chunks.join('\n');
        }
      },
      emphasis: {
        label: {
          show: true,
          fontSize: 12,
          fontWeight: 'bold'
        }
      },
      itemStyle: { borderColor: '#000', borderWidth: 1 },
      labelLine: { show: true },
      data: chartData.map(item => ({
        value: Object.values(item)[0],
        name: Object.keys(item)[0]
      }))
    }
  ]
});
  }, 0);

  L.popup({
  maxWidth: "auto",
  minWidth: "auto",
  maxHeight: "auto",
  autoPan: true,
  closeButton: true,
})
.setLatLng(latlng)
.setContent(wrapper)
.openOn(map);

// Remove Leaflet’s forced width style
const popupContent = document.querySelector('.leaflet-popup-content');
if (popupContent) popupContent.style.width = 'auto';

};



//   // ---- break calculators (copied here to compute, passed up) ----  
  const calculateIndicatorBreaks = (indicatorKey) => {
  if (!statesData) return [];

  const values = statesData.map(s => s[indicatorKey]).filter(v => v != null);
  if (values.length === 0) return [];

  let min = Math.min(...values);
  const max = Math.max(...values);

  if (min > 0 && max > 5) {
    min = 0;
  }

  // Detect if any value is a float
  const hasFloat = values.some(v => v % 1 !== 0);

  // Only integer breaks if min and max are integers and max <= 5 and min >= 0
  const isIntegerRange = Number.isInteger(min) && Number.isInteger(max);
  if (max <= 5 && min >= 0 && isIntegerRange) {
    return Array.from({ length: max - min + 1 }, (_, i) => i + min);
  }

  const numBreaks = 5;

  // Handle rounding factor
  const getRoundingFactor = (v) => {
    if (v === 0) return 1;
    return Math.pow(10, Math.floor(Math.log10(Math.abs(v))) - 1);
  };
  const roundDown = (v, f) => Math.floor(v / f) * f;
  const roundUp = (v, f) => Math.ceil(v / f) * f;

  const rf = getRoundingFactor(max || 1);
  const cleanMin = roundDown(min, rf);
  const cleanMax = roundUp(max, rf);
  const interval = (cleanMax - cleanMin) / numBreaks;

  // Generate breaks: decimals if float exists, integers otherwise
  return Array.from({ length: numBreaks + 1 }, (_, i) => {
    const val = cleanMin + interval * i;
    return hasFloat ? +val.toFixed(2) : Math.round(val);
  });
};


//   //logic to get radive based one breaks
  const getRadiusForValue = (value, breaks) => {
  // Validate breaks_marker: return white if any element is not a valid number
  if (
    !Array.isArray(breaks) ||
    breaks.some(b => isNaN(parseFloat(b)))
  ) {
    return '#ffffff';
  }
    const radii = [1, 4, 8, 12, 16];

    if (value < breaks[1]) return radii[0];
    if (value < breaks[2]) return radii[1];
    if (value < breaks[3]) return radii[2];
    if (value < breaks[4]) return radii[3];
    return radii[4];
  };
//   // logic to get  color based on color for markers
  const getColorForValue = (marker_value, breaks_marker, color_marker) => {
    if (!marker_value) return '#ffffff'; // Default color for null/undefined
      // Validate breaks_marker: return white if any element is not a valid number
  if (
    !Array.isArray(breaks_marker) ||
    breaks_marker.some(b => isNaN(parseFloat(b)))
  ) {
    return '#ffffff';
  }
    const color_markers = selectedPalette;
    const lastIndex = color_markers.length - 1;

    // Use dynamic breaks for color assignment with 5 breaks
    if (marker_value > breaks_marker[4]) return color_markers[lastIndex - 0];
    if (marker_value > breaks_marker[3]) return color_markers[lastIndex - 1];
    if (marker_value > breaks_marker[2]) return color_markers[lastIndex - 2];
    if (marker_value > breaks_marker[1]) return color_markers[lastIndex - 3];
    if (marker_value > breaks_marker[0]) return color_markers[lastIndex - 4];
    return '#ffffff';
  };

//   //bubble , marker , pie logic 
  // useEffect(() => {
  //   // Clear all thematic layers first to ensure clean state
  //   if (indicator3LayerRef.current) indicator3LayerRef.current.clearLayers();
  //   if (indicator4LayerRef.current) indicator4LayerRef.current.clearLayers();
  //   if (indicator5LayerRef.current) indicator5LayerRef.current.clearLayers();
  //   //if (colorLayerRef.current) colorLayerRef.current.remove(); // Remove color layer
  //   //if (patternLayerRef.current) patternLayerRef.current.remove(); // Remove pattern layer

  //   // Clear any existing ECharts layers
  //   if (echartsLayerRef.current) {
  //     echartsLayerRef.current.clearLayers();
  //   }

  //   if (!geoJsonData || !statesData) return;

  //   const normalizedGeoJson = geoJsonData?.features
  //     ? geoJsonData
  //     : { type: "FeatureCollection", features: [geoJsonData] };
  //   map.flyToBounds(L.geoJSON(normalizedGeoJson).getBounds(), { padding: [10, 10] });

  //   if (setMapConfigData?.chartType?.toLowerCase() === 'bubble') {
  //     // Existing bubble chart logic
  //     (normalizedGeoJson.features || []).forEach(feature => {
  //       const stateName = feature.properties.name;
  //       const state = statesData.find(s => s.name === stateName);
  //       const geoLevelCode = feature.properties.geo_level_code;
  //       const multiplier = geoLevelCode === 3 ? 1000 : 5000;
  //       const color = selectedPalette
  //       // const indicator_3_code = setMapConfigData?.indicators[0]?.subIndicator?.code || setMapConfigData?.indicators[0]?.code
  //       // const indicator_4_code = setMapConfigData?.indicators[1]?.subIndicator?.code || setMapConfigData?.indicators[1]?.code
  //       // const indicator_5_code = setMapConfigData?.indicators[2]?.subIndicator?.code || setMapConfigData?.indicators[2]?.code
  //       const indicators = setMapConfigData?.indicators || [];

  //       const getIndicatorCode = (indicator) => {
  //         const i_code = indicator?.code;
  //         const si_code = indicator?.subIndicator?.code;

  //         // If subIndicator exists → `${i_code}#${i_code}_${si_code}`
  //         // Otherwise → `${i_code}#`
  //         if (!i_code) return '';
  //         return si_code ? `${i_code}#${si_code}` : `${i_code}#`;
  //       };

  //       // Safely map the first 3 indicators
  //       const indicator_3_code = getIndicatorCode(indicators[0]);
  //       const indicator_4_code = getIndicatorCode(indicators[1]);
  //       const indicator_5_code = getIndicatorCode(indicators[2]);

  //       if (state && state.lan_lon && (indicator_3_code || indicator_4_code || indicator_5_code)) {
  //         const [lat, lon] = state.lan_lon;
  //         const indicator3Breaks = calculateIndicatorBreaks(indicator_3_code);
  //         const indicator4Breaks = calculateIndicatorBreaks(indicator_4_code);
  //         const indicator5Breaks = calculateIndicatorBreaks(indicator_5_code);
  //         // bubble 1
  //         if (showIndicator3 && state[`${indicator_3_code}`]) {

  //           L.circle([lat, lon], {
  //             color: color[color.length - 4],
  //             fillColor: color[color.length - 4],
  //             fillOpacity: 0.5,
  //             radius: getRadiusForValue(state[indicator_3_code], indicator3Breaks) * multiplier // Adjust multiplier as needed
  //           }).addTo(indicator3LayerRef.current);
  //         }

  //         // bubble 2
  //         if (showIndicator4 && state[`${indicator_4_code}`]) {

  //           L.circle([lat - 0.1, lon - 0.1], {
  //             color: color[color.length - 5],
  //             fillColor: color[color.length - 5],
  //             fillOpacity: 0.5,
  //             radius: getRadiusForValue(state[indicator_4_code], indicator4Breaks) * multiplier // Adjust multiplier as needed
  //           }).addTo(indicator4LayerRef.current);
  //         }

  //         // bubble 3
  //         if (showIndicator5 && state[`${indicator_5_code}`]) {
  //           L.circle([lat + 0.1, lon + 0.1], {
  //             color: color[color.length - 6],
  //             fillColor: color[color.length - 6],
  //             fillOpacity: 0.5,
  //             radius: getRadiusForValue(state[indicator_5_code], indicator5Breaks) * multiplier // Adjust multiplier as needed
  //           }).addTo(indicator5LayerRef.current);
  //         }
  //       }
  //     });
  //   } else if (setMapConfigData?.chartType?.toLowerCase() === 'pie' && showPieChart) {

  //     (normalizedGeoJson.features || []).forEach(feature => {
  //       const stateCode = feature.properties.code;
  //       const state = statesData.find(s => s.code === stateCode);
  //       // const indicator_pie_code = setMapConfigData?.indicators[0]?.subIndicator?.code || setMapConfigData?.indicators[0]?.code; // Assuming 1003 is the third indicator
  //       const indicator = setMapConfigData?.indicators?.[0]; // assuming 1003 is the one you want
  //       const indicator_pie_code = indicator?.code;

  //       if (state && state.lan_lon && indicator_pie_code && state[indicator_pie_code]) {
  //         const [lat, lon] = state.lan_lon;
  //         const chartData = state[indicator_pie_code]

  //         const chartDiv = L.DomUtil.create('div', 'leaflet-echarts-chart');
  //         chartDiv.style.width = '80px';
  //         chartDiv.style.height = '80px';

  //         const chartInstance = echarts.init(chartDiv);
  //         const option = {
  //           series: [
  //             {
  //               name: 'Indicator Data',
  //               type: 'pie',
  //               radius: ['15%', '30%'],
  //               avoidLabelOverlap: false,
  //               label: {
  //                 show: false,
  //                 position: 'center'
  //               },
  //               emphasis: {
  //                 label: {
  //                   show: true,
  //                   fontSize: '10',
  //                   fontWeight: 'bold'
  //                 }
  //               },
  //               itemStyle: {
  //                 borderColor: '#000000',      // 👈 border color
  //                 borderWidth: 1            // 👈 border width
  //               },
  //               labelLine: {
  //                 show: false
  //               },
  //               data: chartData.map(item => ({ value: Object.values(item)[0], name: Object.keys(item)[0] }))
  //             }
  //           ]
  //         };
  //         chartInstance.setOption(option);

  //         const customIcon = L.divIcon({
  //           html: chartDiv,
  //           className: '',
  //           iconSize: [80, 80],
  //           iconAnchor: [40, 40] // Center the icon
  //         });


  //         L.marker([lat, lon], { icon: customIcon }).addTo(echartsLayerRef.current);
  //         const marker = L.marker([lat, lon], { icon: customIcon }).addTo(echartsLayerRef.current);
  //         marker.on('click', () => {
  //           showPiePopup([lat, lon], state); // Use same `showPiePopup` function
  //         });


  //       }
  //     });
  //   } else if (setMapConfigData?.chartType?.toLowerCase() === 'marker') {
  //     // bubble chart logic
  //     (normalizedGeoJson.features || []).forEach(feature => {
  //       const statecode = feature.properties.code;
  //       const state = statesData.find(s => s.code === statecode);
  //       const color_marker = selectedPalette;
  //       // const indicator_3_code = setMapConfigData?.indicators[0]?.subIndicator?.code || setMapConfigData?.indicators[0]?.code;
  //       // const indicator_4_code = setMapConfigData?.indicators[1]?.subIndicator?.code || setMapConfigData?.indicators[1]?.code;
  //       // const indicator_5_code = setMapConfigData?.indicators[2]?.subIndicator?.code || setMapConfigData?.indicators[2]?.code;
  //       const indicators = setMapConfigData?.indicators || [];

  //       const getIndicatorCode = (indicator) => {
  //         const i_code = indicator?.code;
  //         const si_code = indicator?.subIndicator?.code;

  //         // If subIndicator exists → `${i_code}#${i_code}_${si_code}`
  //         // Otherwise → `${i_code}#`
  //         if (!i_code) return '';
  //         return si_code ? `${i_code}#${si_code}` : `${i_code}#`;
  //       };

  //       // Safely map the first 3 indicators
  //       const indicator_3_code = getIndicatorCode(indicators[0]);
  //       const indicator_4_code = getIndicatorCode(indicators[1]);
  //       const indicator_5_code = getIndicatorCode(indicators[2]);

  //       if (state && state.lan_lon && (indicator_3_code || indicator_4_code || indicator_5_code)) {
  //         const [lat, lon] = state.lan_lon;
  //         const indicator3Breaks_marker = calculateIndicatorBreaks(indicator_3_code);
  //         const indicator4Breaks_marker = calculateIndicatorBreaks(indicator_4_code);
  //         const indicator5Breaks_marker = calculateIndicatorBreaks(indicator_5_code);

  //         const shapeSize = 0.3; // Approx. degrees, adjust as needed for zoom
  //         const offset = 0.55;    // Controls how far shapes are spaced apart horizontally

  //         // Triangle (Indicator 3) - center
  //         if (showIndicator3 && state[`${indicator_3_code}`]) {
  //           const color3 = getColorForValue(state[indicator_3_code], indicator3Breaks_marker, color_marker);
  //           const triangle = [
  //             [lat, lon],
  //             [lat - shapeSize, lon - shapeSize],
  //             [lat - shapeSize, lon + shapeSize]
  //           ];
  //           L.polygon(triangle, {
  //             color: 'black',
  //             weight: 1,
  //             fillColor: color3,
  //             fillOpacity: 1
  //           }).addTo(indicator3LayerRef.current);
  //         }

  //         // Square (Indicator 4) - slightly left
  //         if (showIndicator4 && state[`${indicator_4_code}`]) {
  //           const color4 = getColorForValue(state[indicator_4_code], indicator4Breaks_marker, color_marker);
  //           const squareLat = lat - offset;
  //           const squareLon = lon - offset;

  //           const square = [
  //             [squareLat - shapeSize, squareLon - shapeSize],
  //             [squareLat - shapeSize, squareLon + shapeSize],
  //             [squareLat + shapeSize, squareLon + shapeSize],
  //             [squareLat + shapeSize, squareLon - shapeSize]
  //           ];
  //           L.polygon(square, {
  //             color: 'black',
  //             weight: 1,
  //             fillColor: color4,
  //             fillOpacity: 1
  //           }).addTo(indicator4LayerRef.current);
  //         }

  //         // Hexagon (Indicator 5) - slightly right
  //         if (showIndicator5 && state[`${indicator_5_code}`]) {
  //           const color5 = getColorForValue(state[indicator_5_code], indicator5Breaks_marker, color_marker);
  //           const hexLat = lat - offset;
  //           const hexLon = lon + offset;

  //           const hexagon = Array.from({ length: 6 }, (_, i) => {
  //             const angle = (Math.PI / 3) * i;
  //             return [
  //               hexLat + shapeSize * Math.sin(angle),
  //               hexLon + shapeSize * Math.cos(angle)
  //             ];
  //           });

  //           L.polygon(hexagon, {
  //             color: 'black',
  //             weight: 1,
  //             fillColor: color5,
  //             fillOpacity: 1
  //           }).addTo(indicator5LayerRef.current);
  //         }
  //       }
  //     });
  //   }

  // }, [getColorForValue, indicator3LayerRef, indicator4LayerRef, indicator5LayerRef, showPiePopup, calculateIndicatorBreaks, geoJsonData, statesData, showIndicator3, showIndicator4, showIndicator5, showPieChart, selectedPalette, setMapConfigData, map]);

//   // breaks logic for color layer
//   const calculateBreaks = () => {
//   if (!statesData) return [];

//   const colorIndicator = setMapConfigData?.colorIndicator;
//   const i_code = colorIndicator?.code;
//   const si_code = colorIndicator?.subIndicator?.code;

//   const indicator_1_code = si_code
//     ? `${i_code}#${si_code}`
//     : `${i_code}#`;

//   const values = statesData.map(s => s[indicator_1_code]).filter(v => v != null);
//   if (values.length === 0) return [];

//   let min = Math.min(...values);
//   let max = Math.max(...values);

//   // If min > 0 and max > 5, set min to 0
//   if (min > 0 && max > 5) {
//     min = 0;
//   }

//   const numBreaks = 4;

//   // Detect if values contain floats
//   const hasFloat = values.some(v => v % 1 !== 0);

//   // Handle edge case for log10(0) or negative
//   const getRoundingFactor = (v) => {
//     if (v === 0) return 1;
//     return Math.pow(10, Math.floor(Math.log10(Math.abs(v))) - 1);
//   };

//   const roundDown = (v, f) => Math.floor(v / f) * f;
//   const roundUp = (v, f) => Math.ceil(v / f) * f;

//   let rf = getRoundingFactor(max || 1); // fallback to 1 if max=0
//   let cleanMin = roundDown(min, rf);
//   let cleanMax = roundUp(max, rf);

//   const interval = (cleanMax - cleanMin) / numBreaks;

//   // Generate breaks
//   return Array.from({ length: numBreaks + 1 }, (_, i) => {
//     const val = cleanMin + interval * i;
//     return hasFloat ? +val.toFixed(2) : Math.round(val);
//   });
// };

//   // ---- notify parent whenever breaks change ----
//   useEffect(() => {
//     if (!geoJsonData || !statesData) return;
//     const colorBreaks = calculateBreaks();
//     const indicators = setMapConfigData?.indicators || [];

//     const getIndicatorCode = (indicator) => {
//       const i_code = indicator?.code;
//       const si_code = indicator?.subIndicator?.code;

//       // If subIndicator exists → `${i_code}#${i_code}_${si_code}`
//       // Otherwise → `${i_code}#`
//       if (!i_code) return '';
//       return si_code ? `${i_code}#${si_code}` : `${i_code}#`;
//     };

//     // Safely map the first 3 indicators
//     const indicator_3_code = getIndicatorCode(indicators[0]);
//     const indicator_4_code = getIndicatorCode(indicators[1]);
//     const indicator_5_code = getIndicatorCode(indicators[2]);

//     const patternIndicator = setMapConfigData?.patternIndicator;
//     const pi_code = patternIndicator?.code;
//     const psi_code = patternIndicator?.subIndicator?.code;

//     const indicator_2_code = psi_code
//       ? `${pi_code}#${psi_code}`
//       : `${pi_code}#`;
//     const indicatorCodes = [
//       indicator_3_code,
//       indicator_4_code,
//       indicator_5_code,
//       indicator_2_code,
//     ];
//     const indicatorBreaks = {};
//     indicatorCodes.forEach((code) => {
//       if (code) indicatorBreaks[code] = calculateIndicatorBreaks(code);
//     });
//     if (onBreaksCalculated) {
//       onBreaksCalculated(colorBreaks);
//     }
//     if (onIndicatorBreaksCalculated) {
//       onIndicatorBreaksCalculated(indicatorBreaks);
//     }
//   }, [geoJsonData, statesData, setMapConfigData]);

//   // gives color for color layer
//   const getcoloerlayerColor = (color_valuer) => {
//     if (!color_valuer) return '#ffffff'; // Default color for null/undefined
//     const colors = selectedPalette;
//     const breaks = calculateBreaks();

//     // Use dynamic breaks for color assignment with 5 breaks
//     if (color_valuer > breaks[4]) return colors[4];
//     if (color_valuer > breaks[3]) return colors[3];
//     if (color_valuer > breaks[2]) return colors[2];
//     if (color_valuer > breaks[1]) return colors[1];
//     if (color_valuer > breaks[0]) return colors[0];
//     return '#ffffff';
//   };

//   //styles to handle color layer
//   const colorstyle = (feature) => {
//     const state_code = feature.properties.code
//     const state = statesData?.find(s => s.code === state_code);
//     //const indicator_1_code = setMapConfigData?.colorIndicator?.subIndicator?.code || setMapConfigData?.colorIndicator?.code
//     const colorIndicator = setMapConfigData?.colorIndicator;
//     const i_code = colorIndicator?.code;
//     const si_code = colorIndicator?.subIndicator?.code;

//     const indicator_1_code = si_code
//       ? `${i_code}#${si_code}`
//       : `${i_code}#`;

//     const indicator_1_data = state?.[indicator_1_code];
//     return {
//       fillColor: getcoloerlayerColor(indicator_1_data),
//       weight: 2,
//       opacity: 1,
//       color: '#000000', // Border color
//       fillOpacity: 1
//     };
//   };

//   const coloronEachFeature = (feature, layer) => {
//     // empty for not action only color display
//   };

//   // Effect to handle pattern layer
//   useEffect(() => {
//     if (geoJsonData && statesData && showPatternLayer) {
//       // Remove existing pattern layer if it exists
//       if (patternLayerRef.current) {
//         patternLayerRef.current.remove();
//       }

//       // Create new pattern layer
//       patternLayerRef.current =
//         L.geoJSON(geoJsonData, {
//           style: feature => ({
//             color: "#000",
//             weight: 2,
//             fillOpacity: 1
//           }),

//           onEachFeature: (feature, layer) => {
//             layer.on('add', () => {
//               const path = layer._path;
//               //const indicator_2_code = setMapConfigData?.patternIndicator?.subIndicator?.code || setMapConfigData?.patternIndicator?.code
//               const patternIndicator = setMapConfigData?.patternIndicator;
//               const pi_code = patternIndicator?.code;
//               const psi_code = patternIndicator?.subIndicator?.code;

//               const indicator_2_code = psi_code
//                 ? `${pi_code}#${psi_code}`
//                 : `${pi_code}#`;
//               const stateCode = feature.properties.code;
//               const state = statesData.find(s => s.code === stateCode);
//               const patternBreaks = calculateIndicatorBreaks(indicator_2_code);
//               const pattern_value = state?.[indicator_2_code]
//               const patternId = getPatternIdDynamic(pattern_value, patternBreaks);
//               path.setAttribute('fill', `url(#${patternId})`);
//             });
//           }
//         }).addTo(map);
//     } else if (patternLayerRef.current && !showPatternLayer) {
//       // Remove pattern layer if it exists and should not be shown
//       patternLayerRef.current.remove();
//       patternLayerRef.current = null;
//     }

//     return () => {
//       if (patternLayerRef.current) {
//         patternLayerRef.current.remove();
//         patternLayerRef.current = null;
//       }
//     };
//   }, [geoJsonData, statesData, showPatternLayer, map]);

//   //Effect to handle color layer
//   useEffect(() => {
//     if (!geoJsonData || !statesData || !map) return;

//     // Remove existing layer if any
//     if (colorLayerRef.current) {
//       colorLayerRef.current.remove();
//     }

//     if (showColorLayer) {
//       const newColorLayer = L.geoJSON(geoJsonData, {
//         style: colorstyle,
//         onEachFeature: coloronEachFeature
//       });

//       newColorLayer.addTo(map);
//       colorLayerRef.current = newColorLayer;
//       colorLayerRef.current.bringToBack();
//     }

//     return () => {
//       if (colorLayerRef.current) {
//         colorLayerRef.current.remove();
//         colorLayerRef.current = null;
//       }
//     };
//   }, [geoJsonData, statesData, showColorLayer, selectedPalette, map]);

//   const transparentStyle = () => {
//     return {
//       fillColor: '#ffffff', // Make it transparent
//       weight: 2, // Keep border weight
//       opacity: 1,
//       color: '#000000', // Border color
//       fillOpacity: 0 // Fully transparent fill
//     };
//   };

//   // Modified onEachFeature for data display
//   const onEachFeatureWithAllData = (feature, layer) => {
//     const stateName = feature.properties.name;
//     const state = statesData?.find(s => s.name === stateName);
//     //const indicator_1_code = setMapConfigData?.colorIndicator?.subIndicator?.code || setMapConfigData?.colorIndicator?.code
//     const colorIndicator = setMapConfigData?.colorIndicator;
//     const i_code = colorIndicator?.code;
//     const si_code = colorIndicator?.subIndicator?.code;

//     const indicator_1_code = si_code
//       ? `${i_code}#${si_code}`
//       : `${i_code}#`;
//     const indicator_1_name = setMapConfigData?.colorIndicator?.label
//     const indicator_1_subname = setMapConfigData?.colorIndicator?.subIndicator?.label
//     //const indicator_2_code = setMapConfigData?.patternIndicator?.subIndicator?.code || setMapConfigData?.patternIndicator?.code
//     const patternIndicator = setMapConfigData?.patternIndicator;
//     const pi_code = patternIndicator?.code;
//     const psi_code = patternIndicator?.subIndicator?.code;

//     const indicator_2_code = psi_code
//       ? `${pi_code}#${psi_code}`
//       : `${pi_code}#`;

//     const indicator_2_name = setMapConfigData?.patternIndicator?.label
//     const indicator_2_subname = setMapConfigData?.patternIndicator?.subIndicator?.label
//     //const indicator_3_code = setMapConfigData?.indicators[0]?.subIndicator?.code || setMapConfigData?.indicators[0]?.code
//     const indicator_3_name = setMapConfigData?.indicators[0]?.label
//     const indicator_3_subname = setMapConfigData?.indicators[0]?.subIndicator?.label
//     //const indicator_4_code = setMapConfigData?.indicators[1]?.subIndicator?.code || setMapConfigData?.indicators[1]?.code
//     const indicator_4_name = setMapConfigData?.indicators[1]?.label
//     const indicator_4_subname = setMapConfigData?.indicators[1]?.subIndicator?.label
//     //const indicator_5_code = setMapConfigData?.indicators[2]?.subIndicator?.code || setMapConfigData?.indicators[2]?.code
//     const indicator_5_name = setMapConfigData?.indicators[2]?.label
//     const indicator_5_subname = setMapConfigData?.indicators[2]?.subIndicator?.label
//     const indicators = setMapConfigData?.indicators || [];

//     const getIndicatorCode = (indicator) => {
//       const i_code = indicator?.code;
//       const si_code = indicator?.subIndicator?.code;

//       // If subIndicator exists → `${i_code}#${i_code}_${si_code}`
//       // Otherwise → `${i_code}#`
//       if (!i_code) return '';
//       return si_code ? `${i_code}#${si_code}` : `${i_code}#`;
//     };

//     // Safely map the first 3 indicators
//     const indicator_3_code = getIndicatorCode(indicators[0]);
//     const indicator_4_code = getIndicatorCode(indicators[1]);
//     const indicator_5_code = getIndicatorCode(indicators[2]);

//     let popupContent = `
//       <div style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; min-width: 220px;">
//         <div style="background-color: #2c3e50; color: white; padding: 6px 10px; border-radius: 4px 4px 0 0; font-size: 14px; font-weight: bold;">
//           ${stateName}
//         </div>
//         <div style="padding: 8px; background: #f9f9f9; border: 1px solid #ccc; border-top: none; border-radius: 0 0 4px 4px;">
//     `;

//     const addIndicatorRow = (subname, name, value) => {
//       return `
//           <div style="margin-bottom: 6px; font-size: 13px;">
//             <span style="color: #2980b9; font-weight: 500;">${subname || ''}</span>
//             ${name}: <strong style="color: #333;">${value}</strong>
//           </div>
//         `;
//     };

//     if (state && showColorLayer && indicator_1_code && state[indicator_1_code] != null) {
//       popupContent += addIndicatorRow(indicator_1_subname, indicator_1_name, state[indicator_1_code]);
//     }

//     if (state && showPatternLayer && indicator_2_code && state[indicator_2_code] != null) {
//       popupContent += addIndicatorRow(indicator_2_subname, indicator_2_name, state[indicator_2_code]);
//     }

//     if (state && showIndicator3 && indicator_3_code && state[indicator_3_code] != null) {
//       popupContent += addIndicatorRow(indicator_3_subname, indicator_3_name, state[indicator_3_code]);
//     }

//     if (state && showIndicator4 && indicator_4_code && state[indicator_4_code] != null) {
//       popupContent += addIndicatorRow(indicator_4_subname, indicator_4_name, state[indicator_4_code]);
//     }

//     if (state && showIndicator5 && indicator_5_code && state[indicator_5_code] != null) {
//       popupContent += addIndicatorRow(indicator_5_subname, indicator_5_name, state[indicator_5_code]);
//     }

//     popupContent += `
//         </div>
//       </div>
//     `;

//     layer.on({
//       mouseover: (e) => {
//         layer.bindPopup(popupContent, {
//           offset: L.point(0, -20), // move right and up a little
//           closeButton: false,
//           className: 'custom-popup'
//         }).openPopup();
//       },
//       mouseout: (e) => {
//         layer.closePopup();
//       },
//     });
//   };

//   //popup layer for data display
//   useEffect(() => {
//     if (!geoJsonData || !statesData || !map) return;

//     // Remove existing popup layer if any
//     if (popupLayerRef.current) {
//       popupLayerRef.current.remove();
//     }

//     // Create new popup/transparent interaction layer
//     const newPopupLayer = L.geoJSON(geoJsonData, {
//       style: transparentStyle,
//       onEachFeature: onEachFeatureWithAllData
//     }).addTo(map);

//     // Store reference
//     popupLayerRef.current = newPopupLayer;

//     // Bring popup layer to front so it catches mouse events
//     newPopupLayer.bringToFront();

//     return () => {
//       if (popupLayerRef.current) {
//         popupLayerRef.current.remove();
//         popupLayerRef.current = null;
//       }
//     };
//   }, [
//     geoJsonData,
//     statesData,
//     map,
//     showIndicator3,
//     showIndicator4,
//     showIndicator5,
//     showColorLayer,
//     showPatternLayer,
//     selectedPalette
//   ]);

//   //layer stacking 
//   useEffect(() => {
//     if (!map) return;

//     // Remove all thematic layers
//     if (colorLayerRef.current) map.removeLayer(colorLayerRef.current);
//     if (patternLayerRef.current) map.removeLayer(patternLayerRef.current);
//     if (echartsLayerRef.current) map.removeLayer(echartsLayerRef.current);
//     if (indicator3LayerRef.current) map.removeLayer(indicator3LayerRef.current);
//     if (indicator4LayerRef.current) map.removeLayer(indicator4LayerRef.current);
//     if (indicator5LayerRef.current) map.removeLayer(indicator5LayerRef.current);

//     // Re-add them in specific order
//     if (showColorLayer && colorLayerRef.current) colorLayerRef.current.addTo(map);
//     if (showPatternLayer && patternLayerRef.current) patternLayerRef.current.addTo(map);
//     if (setShowPieChart && echartsLayerRef.current) echartsLayerRef.current.addTo(map);
//     if (showIndicator3 && indicator3LayerRef.current) indicator3LayerRef.current.addTo(map);
//     if (showIndicator4 && indicator4LayerRef.current) indicator4LayerRef.current.addTo(map);
//     if (showIndicator5 && indicator5LayerRef.current) indicator5LayerRef.current.addTo(map);

//     // Popup interaction layer should always be on top
//     if (popupLayerRef.current) popupLayerRef.current.bringToFront();
//     if (colorLayerRef.current) colorLayerRef.current.bringToBack();

//   }, [indicator3LayerRef, indicator4LayerRef, indicator5LayerRef, setShowPieChart, showColorLayer, showPatternLayer, showIndicator3, showIndicator4, showIndicator5, showPieChart, geoJsonData, map
//   ]);

//   // popup for pie chart
//   useEffect(() => {
//     if (setMapConfigData?.chartType?.toLowerCase() === 'pie' && geoJsonData) {
//       const normalizedGeoJson = geoJsonData?.features
//         ? geoJsonData
//         : { type: "FeatureCollection", features: [geoJsonData] };
//       L.geoJSON(normalizedGeoJson.features, {
//         style: transparentStyle,
//         onEachFeature: function (feature, layer) {
//           layer.on('click', function (e) {
//             const stateName = feature.properties.name;
//             const state = statesData.find(s => s.name === stateName);
//             const indicator_pie_code = setMapConfigData?.indicators[0]?.subIndicator?.code || setMapConfigData?.indicators[0]?.code;

//             if (state && state[indicator_pie_code]) {
//               showPiePopup(e.latlng, state);
//             }
//           });
//         }
//       }).addTo(map);
//     }
//   }, [showPieChart, geoJsonData]);


useEffect(() => {
  if ( !statesData) return;
    //   const normalizedGeoJson = geoJsonData?.features
    //   ? geoJsonData
    //   : { type: "FeatureCollection", features: [geoJsonData] };
    // map.flyToBounds(L.geoJSON(normalizedGeoJson).getBounds(), { padding: [10, 10] });

  // Extract config
  const chartType = setMapConfigData?.chartType?.toLowerCase();
  const indicator = setMapConfigData?.indicator;
  const indicator_name = indicator?.label;
  const i_code = indicator?.code;
    const si_code = indicator?.subIndicator?.code;

    const code = si_code
      ? `${i_code}#${si_code}`
      : `${i_code}#`;
    const picode = i_code
  if (!code) return;

  // 🔸 Compute breaks once for all states
  const breaks = calculateIndicatorBreaks(code);

  // 🔹 Loop through state data
  statesData.forEach((state) => {
    const [lat, lon] = state.lan_lon || [];
    const value = state[code];
const geoLevelOffsets = {
  1: 5500,  // National
  2: 2000,  // State / UT
  3: 300,  // District
};

// Get the geo_level from selectedLocation
const geoLevel = setMapConfigData.selectedLocation.geo_level;

// Use mapping to get offset, defaulting to 0 if geo_level is unknown
const offset = geoLevelOffsets[geoLevel] || 0;

    if (!lat || !lon ) return;

    const fillColor = getColorForValue(value, breaks, selectedPalette);
    const radius = getRadiusForValue(value, breaks);


    // ===============================
    // 🔵 1. Bubble Chart
    // ===============================
    if (chartType === "bubble") {
      L.circle([lat, lon], {
        color: selectedPalette[selectedPalette.length-2],
        fillColor: selectedPalette[selectedPalette.length-2],
        fillOpacity: 0.6,
        radius: radius * offset, // radius scaling multiplier
      })
        .addTo(map)
        .bindTooltip(
          `<div><strong>${state.name}</strong><br/>${indicator_name}: ${value}</div>`,
          { direction: "top", offset: [0, -10] }
        );
    }

    // ===============================
    // 🥧 2. Pie Chart
    // ===============================
    else if (chartType === "pie" && showPieChart) {
      const chartDiv = L.DomUtil.create("div", "leaflet-echarts-chart");
      chartDiv.style.width = "80px";
      chartDiv.style.height = "80px";
      let pie_value = state[picode]|| 0;

      const chartInstance = echarts.init(chartDiv);
      const option = {
        series: [
          {
            name: "Indicator",
            type: "pie",
            radius: ["15%", "35%"],
            label: { show: false },
            itemStyle: {
              borderColor: "#000000",
              borderWidth: 1,
            },
            data: pie_value.map(item => ({
              value: Object.values(item)[0],
              name: Object.keys(item)[0]
            })),
          },
        ],
      };
      chartInstance.setOption(option);

      const customIcon = L.divIcon({
        html: chartDiv,
        className: "",
        iconSize: [80, 80],
        iconAnchor: [40, 40],
      });

      const marker = L.marker([lat, lon], { icon: customIcon }).addTo(map);
      marker.on("click", () => showPiePopup([lat, lon], state));
    }

    // ===============================
    // 📍 3. Marker Chart
    // ===============================

else if (chartType === "marker") {
  const geoLevelOffsets = {
  1: 0.25,  // National
  2: 0.1,  // State / UT
  3: 0.01,  // District
};

// Get the geo_level from selectedLocation
const geoLevel = setMapConfigData.selectedLocation.geo_level || 1;
  // Triangle centered at [lat, lon]
  const size = geoLevelOffsets[geoLevel] || 0.01; // adjust to make triangle smaller or larger (degrees)
  
  const triangleCoords = [
    [lat + size, lon],          // top vertex
    [lat - size, lon - size],   // bottom-left
    [lat - size, lon + size],   // bottom-right
  ];

  L.polygon(triangleCoords, {
    color: 'black',       // border color
    weight: 1,            // border thickness
    fillColor: fillColor, // from your getColorForValue()
    fillOpacity: 1,
  })
    .addTo(map)
    .bindTooltip(
  `
  <div style="
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    border-radius: 4px;
    box-shadow: 0 2px 6px rgba(0,0,0,0.2);
    overflow: hidden;
    min-width: 180px;
  ">
    <div style="
      background-color: #2c3e50;
      color: white;
      padding: 6px 10px;
      font-size: 13px;
      font-weight: 600;
      border-radius: 4px 4px 0 0;
    ">
      ${state.name}
    </div>
    <div style="
      background: #f9f9f9;
      color: #333;
      padding: 6px 10px;
      font-size: 13px;
      border: 1px solid #ccc;
      border-top: none;
      border-radius: 0 0 4px 4px;
    ">
      ${indicator_name}: <strong style="color: #333;">${value}</strong>
    </div>
  </div>
  `,
  { direction: "top", offset: [0, -10], sticky: true }
);}});
}, [
  statesData,
  map,
  setMapConfigData,
  selectedPalette,
  showPieChart,
  showPiePopup,
]);
useEffect(() => {
  if ( !statesData) return;

  // Extract config
  const chartType = setMapConfigData?.chartType?.toLowerCase();
  const indicator = setMapConfigData?.indicator;
  const i_code = indicator?.code;
    const si_code = indicator?.subIndicator?.code;

    const code = si_code
      ? `${i_code}#${si_code}`
      : `${i_code}#`;
const breaks = calculateIndicatorBreaks(code);
setUlbMapBreak(breaks)
},[statesData,setMapConfigData])
  return (
    <>
      {/* <PatternDefs /> */}
      {geoJsonData && (
        <>
          <GeoJSON
            key={JSON.stringify(geoJsonData)}
            data={geoJsonData}
            style={{ color: "#000", weight: 1, fillColor: "#fff", fillOpacity: 0.5 }}
            //style={transparentStyle}
            //onEachFeature={onEachFeatureWithAllData}
          />
        </>
      )}
      {showController&&(<ColorPaletteSelection onSelectPalette={setSelectedPalette} selectedPalette={selectedPalette} />)}
      
      {/* <LayerSelection
        selectedPalette={selectedPalette}
        setSelectedPalette={setSelectedPalette}
        showIndicator3={showIndicator3}
        setShowIndicator3={setShowIndicator3}
        showIndicator4={showIndicator4}
        setShowIndicator4={setShowIndicator4}
        showIndicator5={showIndicator5}
        setShowIndicator5={setShowIndicator5}
        showColorLayer={showColorLayer}
        setShowColorLayer={setShowColorLayer}
        showPatternLayer={showPatternLayer}
        setShowPatternLayer={setShowPatternLayer}
        setMapConfigData={setMapConfigData}
        showPieChart={showPieChart}
        setShowPieChart={setShowPieChart}
      /> */}
    </>
  );
};

// ================= MapWorkspace =================
const MapWorkspace = ({ geoJsonData, statesData, selectedPalette, showIndicator3,
  showIndicator4, showIndicator5, showColorLayer, showPatternLayer, setSelectedPalette, setGeoJsonData, setStatesData,
  setShowIndicator3, setShowIndicator4, setShowIndicator5, setShowColorLayer, setShowPatternLayer, setMapConfigData,
  onBreaksCalculated, onIndicatorBreaksCalculated, showPieChart, setShowPieChart, mapRef ,setUlbMapBreak,showController}) => {
  const indicator3LayerRef = useRef(null);
  const indicator4LayerRef = useRef(null);
  const indicator5LayerRef = useRef(null);
  const internalMapRef = useRef();
  useImperativeHandle(mapRef, () => ({
    forceResize: () => {
      if (internalMapRef.current) {
        setTimeout(() => {
          internalMapRef.current.invalidateSize();
        }, 1000); // allow layout to settle
      }
    }
  }));
  // Custom Zoom Control Component
  const ZoomControl = () => {
    const map = useMap();

    const zoomIn = () => {
      map.zoomIn();
    };

    const zoomOut = () => {
      map.zoomOut();
    };

    return (
      <div className="position-absolute" style={{ top: '10px', left: '50px', zIndex: 1000 }}>
        <div
          className="bg-white border rounded shadow-sm"
          style={{
            width: '40px',
            height: '80px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '5px'
          }}
        >
          <button
            className="btn btn-sm rounded-circle d-flex align-items-center justify-content-center"
            onClick={zoomIn}
            title="Zoom In"
            style={{
              width: '35px',
              height: '35px',
              backgroundColor: 'white',
              border: '1px solid #748194',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '5px',
              padding: '2px',
              // boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}
          >
            <FaPlus size={16} color="#6c757d" />
          </button>
          <button
            className="btn btn-sm rounded-circle d-flex align-items-center justify-content-center"
            onClick={zoomOut}
            title="Zoom Out"
            style={{
              width: '35px',
              height: '35px',
              backgroundColor: 'white',
              border: '1px solid #748194',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '2px',
              // boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}
          >
            <FaMinus size={16} color="#6c757d" />
          </button>
        </div>
      </div>
    );
  };

  const PanControl = ({ selectedFeature }) => {
    const map = useMap();
    const panDistance = 0.5; // degrees

    // Dynamically reset map to selected feature using its bounds
    const resetToSelectedFeature = () => {
      if (selectedFeature && selectedFeature) {
        let geojsonLayer = geoJSON(selectedFeature);
        map.fitBounds(geojsonLayer.getBounds()); // sets center & zoom dynamically
      }
    };

    // Auto-center map whenever the selected feature changes
    useEffect(() => {
      resetToSelectedFeature();
    }, [selectedFeature]);

    const panNorth = () => {
      const currentCenter = map.getCenter();
      map.setView([currentCenter.lat + panDistance, currentCenter.lng], map.getZoom());
    };

    const panSouth = () => {
      const currentCenter = map.getCenter();
      map.setView([currentCenter.lat - panDistance, currentCenter.lng], map.getZoom());
    };

    const panEast = () => {
      const currentCenter = map.getCenter();
      map.setView([currentCenter.lat, currentCenter.lng + panDistance], map.getZoom());
    };

    const panWest = () => {
      const currentCenter = map.getCenter();
      map.setView([currentCenter.lat, currentCenter.lng - panDistance], map.getZoom());
    };

    const zoomIn = () => map.zoomIn();
    const zoomOut = () => map.zoomOut();

    return (
      <div className="position-absolute" style={{ top: '100px', left: '10px', zIndex: 1000 }}>
        <div
          className="bg-white border rounded-circle shadow-sm"
          style={{
            width: '120px',
            height: '120px',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {/* Home Button - Center */}
          <button
            className="btn btn-sm btn-outline-primary rounded-circle"
            onClick={resetToSelectedFeature}
            title="Reset to selected feature"
            style={{
              width: '35px',
              height: '35px',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-52%, -52%)',
              zIndex: 2
            }}
          >
            <FaCircle size={14} style={{ transform: 'translate(-2px, -1px)' }} />
          </button>

          {/* North Button */}
          <button
            className="btn btn-sm btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center"
            onClick={panNorth}
            title="Pan North"
            style={{
              position: 'absolute',
              top: '5px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '35px',
              height: '35px',
            }}
          >
            <FaChevronUp size={45} />
          </button>

          {/* East Button */}
          <button
            className="btn btn-sm btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center"
            onClick={panEast}
            title="Pan East"
            style={{
              position: 'absolute',
              right: '5px',
              top: '50%',
              transform: 'translateY(-51%)',
              width: '35px',
              height: '35px',
            }}
          >
            <FaChevronRight size={40} />
          </button>

          {/* South Button */}
          <button
            className="btn btn-sm btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center"
            onClick={panSouth}
            title="Pan South"
            style={{
              position: 'absolute',
              bottom: '5px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '35px',
              height: '35px',
            }}
          >
            <FaChevronDown size={45} />
          </button>

          {/* West Button */}
          <button
            className="btn btn-sm btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center"
            onClick={panWest}
            title="Pan West"
            style={{
              position: 'absolute',
              left: '5px',
              top: '50%',
              transform: 'translateY(-52%)',
              width: '35px',
              height: '35px',
            }}
          >
            <FaChevronLeft size={40} />
          </button>
        </div>
      </div>
    );
  };
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <MapContainer
        center={[20.5937, 78.9629]}
        zoom={5}
        minZoom={4}
        //maxBounds={[[6.75, 68.0], [40.07, 97.5]]}
        //maxBoundsViscosity={1.0}
        dragging={false}          // Disables panning via drag
        scrollWheelZoom={false}   // Disables zoom via mouse wheel
        doubleClickZoom={false}   // Disables zoom via double click
        boxZoom={false}           // Disables zoom via box selection
        zoomControl={false}        // Zoom control buttons still shown
        whenCreated={(mapInstance) => {
          mapRef.current = mapInstance;
        }}
        style={{ height: '100%', width: '100%', backgroundColor: 'white' }}
      >
        {showController && (
          <>
          {/* Custom Zoom Controls - Top Left */}
          <ZoomControl />

          {/* Custom Pan Controls */}
          <PanControl selectedFeature={geoJsonData} />
          </>
        )}
        <MapContent
          geoJsonData={geoJsonData}
          statesData={statesData}
          selectedPalette={selectedPalette}
          indicator3LayerRef={indicator3LayerRef}
          indicator4LayerRef={indicator4LayerRef}
          indicator5LayerRef={indicator5LayerRef}
          showIndicator3={showIndicator3}
          showIndicator4={showIndicator4}
          showIndicator5={showIndicator5}
          setUlbMapBreak={setUlbMapBreak}
          setSelectedPalette={setSelectedPalette}
          setGeoJsonData={setGeoJsonData}
          setStatesData={setStatesData}
          setShowIndicator3={setShowIndicator3}
          showPieChart={showPieChart} // Pass new prop
          setShowPieChart={setShowPieChart} // Pass new prop
          setShowIndicator4={setShowIndicator4}
          setShowIndicator5={setShowIndicator5}
          showColorLayer={showColorLayer}
          setShowColorLayer={setShowColorLayer}
          showPatternLayer={showPatternLayer}
          setShowPatternLayer={setShowPatternLayer}
          setMapConfigData={setMapConfigData}
          onBreaksCalculated={onBreaksCalculated}
          onIndicatorBreaksCalculated={onIndicatorBreaksCalculated}
          showController={showController} // Pass new prop
        />
      </MapContainer>
    </div>
  );
};

export default MapWorkspace;
